/**
 * Canonical theme-mode preference + resolution (docs/ui-design-tokens.md §7).
 *
 * One preference vocabulary, one resolved runtime mode, one persisted key:
 *
 *   ThemePreference  = 'system' | 'light' | 'dark'
 *   ResolvedThemeMode = 'light' | 'dark'
 *   persisted key     = 'vrs_theme_mode'   (fresh default 'system')
 *
 * Legacy keys are read as compatibility fallbacks only (never written, never
 * migrated on read):
 *   vrs_theme_mode -> vrs_dark_mode -> vrs_interpreter_theme -> 'dark'
 *
 * The core is platform-neutral: storage access is INJECTED via the read/write
 * callbacks, so this module never hardwires localStorage/AsyncStorage. The web
 * OS-theme resolver + subscription helper at the bottom are the only
 * `window`-dependent parts; they are lazy (function-scoped) so importing this
 * module on React Native is safe — native resolves `system` via
 * `useColorScheme()` (see useTenantTheme) and does not call these.
 */
/** Canonical persisted key. Going forward this is the ONLY key written. */
export const THEME_MODE_STORAGE_KEY = 'vrs_theme_mode';
/** Legacy keys — read-only compatibility fallbacks (retired in a later package). */
export const LEGACY_DARK_MODE_STORAGE_KEY = 'vrs_dark_mode';
export const LEGACY_INTERPRETER_THEME_STORAGE_KEY = 'vrs_interpreter_theme';
/** Canonical fresh default: follow the user's OS (resolves to MCG Light/Dark). */
export const DEFAULT_THEME_PREFERENCE = 'system';
/**
 * Normalize a raw stored value to a ThemePreference.
 *
 * @param {unknown} value - The raw value to validate.
 * @returns {ThemePreference|null} The value if it is a valid preference, else null.
 */
export function normalizeThemePreference(value) {
    return value === 'system' || value === 'light' || value === 'dark' ? value : null;
}
/**
 * Read the persisted theme preference with legacy fallback precedence:
 * `vrs_theme_mode` -> `vrs_dark_mode` -> `vrs_interpreter_theme` -> `'dark'`.
 *
 * Malformed values at any level are skipped (never returned).
 *
 * @param {ThemeStorageReader} read - Injected storage reader.
 * @returns {ThemePreference} The effective preference.
 */
export function getThemePreference(read, defaultPreference = DEFAULT_THEME_PREFERENCE) {
    const canonical = normalizeThemePreference(read(THEME_MODE_STORAGE_KEY));
    if (canonical) {
        return canonical;
    }
    const legacyDark = normalizeThemePreference(read(LEGACY_DARK_MODE_STORAGE_KEY));
    if (legacyDark) {
        return legacyDark;
    }
    const legacyInterpreter = normalizeThemePreference(read(LEGACY_INTERPRETER_THEME_STORAGE_KEY));
    if (legacyInterpreter) {
        return legacyInterpreter;
    }
    return defaultPreference;
}
/**
 * Resolve a preference to a concrete light/dark mode.
 *
 * @param {ThemePreference|null|undefined} preference - The preference to resolve.
 * @param {ResolvedThemeMode|null|undefined} systemMode - The OS mode used when
 *   the preference is 'system' (e.g. getSystemThemeMode() on web, useColorScheme() on native).
 * @returns {ResolvedThemeMode} 'light' or 'dark'.
 */
export function resolveThemeMode(preference, systemMode) {
    const normalized = normalizeThemePreference(preference);
    switch (normalized) {
        case 'light':
            return 'light';
        case 'dark':
            return 'dark';
        case 'system':
            return systemMode === 'light' ? 'light' : 'dark';
        default:
            // null / malformed preference -> canonical dark default.
            return 'dark';
    }
}
/**
 * Write the canonical preference. Writes ONLY `vrs_theme_mode` — legacy keys
 * are never written and never dual-written.
 *
 * @param {ThemeStorageWriter} write - Injected storage writer.
 * @param {ThemePreference} preference - The preference to persist.
 */
export function setThemePreference(write, preference) {
    write(THEME_MODE_STORAGE_KEY, preference);
}
/**
 * Resolve the current OS color-scheme preference (web only).
 *
 * @returns {ResolvedThemeMode} 'dark' when the OS prefers dark, otherwise 'light'
 *   (and 'dark' as a safe default when matchMedia is unavailable).
 */
export function getSystemThemeMode() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
/**
 * Subscribe to OS color-scheme changes (web only). Returns an unsubscribe
 * function; on platforms without matchMedia the subscription is a no-op and the
 * returned unsubscribe is a no-op.
 *
 * @param {(mode: ResolvedThemeMode) => void} callback - Invoked with the new mode on change.
 * @returns {() => void} Unsubscribe function.
 */
export function subscribeToSystemTheme(callback) {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => { };
    }
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => callback(query.matches ? 'dark' : 'light');
    query.addEventListener('change', handler);
    return () => query.removeEventListener('change', handler);
}
